import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Loader2, Save, X, Plus, Trash2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../components/ui/dialog";

const DEFAULT_CATEGORIES = ["occupancy", "adr", "revpar", "tourism", "investment"];

export default function AdminCityData() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState("");
    const [importing, setImporting] = useState(false);
    const fileRef = useRef(null);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/city-data");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setImporting(true);
        try {
            const { data } = await api.post("/admin/city-data/import-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${data.cities_updated} il güncellendi (${data.rows} satır)`);
            await fetchItems();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setImporting(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const filtered = items.filter((it) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            it.city_code?.includes(q) ||
            (it.city_name?.tr || "").toLowerCase().includes(q)
        );
    });

    return (
        <div className="space-y-6" data-testid="admin-city-data">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy">İl Verileri</h1>
                    <p className="text-sm text-deep-blue/70">81 il × özel metrikler. CSV ile toplu güncelleme yapılabilir.</p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        placeholder="İl ara..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="text-sm rounded-md border border-slate-300 px-3 py-2"
                        data-testid="city-search"
                    />
                    <label
                        className={`bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 cursor-pointer ${importing ? "opacity-70" : ""}`}
                        data-testid="city-csv-import"
                    >
                        {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        CSV İçe Aktar
                        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
                    </label>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-light-bg text-deep-blue">
                        <tr>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold w-20">Kod</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">İl Adı</th>
                            <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Metrik Sayısı</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Son Güncelleme</th>
                            <th className="px-4 py-2.5 w-32"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Yükleniyor...</td></tr>
                        ) : filtered.map((c) => (
                            <tr key={c.city_code} className="hover:bg-light-bg/50">
                                <td className="px-4 py-2 font-data font-semibold text-deep-blue">{c.city_code}</td>
                                <td className="px-4 py-2 text-dark-navy font-medium">{c.city_name?.tr}</td>
                                <td className="px-4 py-2 text-right font-data text-deep-blue">{(c.metrics || []).length}</td>
                                <td className="px-4 py-2 text-xs text-deep-blue/60 font-data">
                                    {c.updated_at ? new Date(c.updated_at).toLocaleString("tr-TR") : "—"}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <button
                                        onClick={() => setEditing(c)}
                                        className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded text-deep-blue hover:bg-light-bg"
                                        data-testid={`city-edit-${c.city_code}`}
                                    >
                                        Düzenle →
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <CityEditDialog
                    city={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        fetchItems();
                    }}
                />
            )}
        </div>
    );
}

function CityEditDialog({ city, onClose, onSaved }) {
    const [metrics, setMetrics] = useState(city.metrics?.length ? [...city.metrics] : []);
    const [saving, setSaving] = useState(false);

    const add = () => setMetrics((p) => [...p, { category: DEFAULT_CATEGORIES[0], value: 0, period: new Date().toISOString().slice(0, 10) }]);
    const update = (i, k, v) => setMetrics((p) => p.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
    const remove = (i) => setMetrics((p) => p.filter((_, idx) => idx !== i));

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/city-data/${city.city_code}`, {
                metrics: metrics.map((m) => ({
                    category: m.category,
                    value: parseFloat(m.value) || 0,
                    period: m.period || new Date().toISOString(),
                })),
            });
            toast.success("Kaydedildi");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{city.city_code} · {city.city_name?.tr}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                    {metrics.length === 0 && (
                        <div className="text-sm text-slate-500 py-4 text-center">Metrik yok. Ekleyin.</div>
                    )}
                    {metrics.map((m, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-4">
                                <span className="text-[11px] uppercase tracking-wider text-deep-blue/80 font-semibold">Kategori</span>
                                <input
                                    list="city-cats"
                                    value={m.category}
                                    onChange={(e) => update(i, "category", e.target.value)}
                                    className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm outline-none"
                                    data-testid={`city-metric-cat-${i}`}
                                />
                            </div>
                            <div className="col-span-3">
                                <span className="text-[11px] uppercase tracking-wider text-deep-blue/80 font-semibold">Değer</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={m.value}
                                    onChange={(e) => update(i, "value", e.target.value)}
                                    className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm outline-none font-data"
                                />
                            </div>
                            <div className="col-span-4">
                                <span className="text-[11px] uppercase tracking-wider text-deep-blue/80 font-semibold">Dönem</span>
                                <input
                                    type="date"
                                    value={(m.period || "").slice(0, 10)}
                                    onChange={(e) => update(i, "period", e.target.value)}
                                    className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm outline-none"
                                />
                            </div>
                            <div className="col-span-1">
                                <button
                                    onClick={() => remove(i)}
                                    className="p-1.5 rounded text-data-red hover:bg-red-50"
                                    aria-label="Remove"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                    <datalist id="city-cats">
                        {DEFAULT_CATEGORIES.map((c) => <option key={c} value={c} />)}
                    </datalist>
                    <button
                        onClick={add}
                        className="text-sm text-teal-700 font-semibold inline-flex items-center gap-1 hover:text-teal"
                        data-testid="city-metric-add"
                    >
                        <Plus className="w-4 h-4" /> Metrik ekle
                    </button>
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="text-sm px-4 py-2 rounded-md border border-slate-300 hover:bg-light-bg" type="button">
                        İptal
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="bg-dark-navy text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-70"
                        data-testid="city-save-btn"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Kaydet
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
